import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { getBusinessProposalByToken, updateProposalStatus, createProposalSignature, getHubspotProposalById } from '@/services/business-opportunities';
import { signViaDocuSign } from '@/services/docusign';
import { BusinessProposal, ProposalStatus } from '@/types/business-opportunities';
import { formatCurrency } from '@/lib/utils';
import { AlertCircle, Check, Download, ExternalLink, FileText, Loader2, ThumbsDown, ThumbsUp } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

type ProposalWithDetails = BusinessProposal & {
  opportunity: {
    id: string;
    name: string;
    customer_id: string;
    organization_id: string;
    currency: string;
    customer: {
      id: string;
      name: string;
      business_identification_number?: string;
      legal_name?: string;
      address?: string;
      city?: string;
      state?: string;
      zip?: string;
    };
  };
  proposal_items: Array<{
    id: string;
    description: string;
    quantity: number;
    unit_price: number;
    unit_measure: string;
    discount_percentage: number;
    total_price: number;
  }>;
};

const statusLabels = {
  draft: 'Rascunho',
  sent: 'Enviada',
  viewed: 'Visualizada',
  accepted: 'Aceita',
  rejected: 'Rejeitada',
  expired: 'Expirada'
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  viewed: 'bg-purple-100 text-purple-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  expired: 'bg-amber-100 text-amber-800'
};

const PublicProposalView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [proposal, setProposal] = useState<ProposalWithDetails | null>(null);

  const formatUnitMeasure = (unitMeasure?: string) => {
    if (!unitMeasure) return '';
    
    const unitMap: Record<string, string> = {
      'user': 'usuário',
      'hour': 'hora',
      'gb': 'GB',
      'license': 'licença',
      'seat': 'assento',
      'instance': 'instância',
      'api_call': 'chamada de API',
      'transaction': 'transação'
    };
    
    return unitMap[unitMeasure.toLowerCase()] || unitMeasure;
  };
  const [isLoading, setIsLoading] = useState(true);
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showDocuSignDialog, setShowDocuSignDialog] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [signerCompany, setSignerCompany] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signature, setSignature] = useState<SignatureCanvas | null>(null);
  const [docuSignUrl, setDocuSignUrl] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('details');
  const pdfIframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const fetchProposal = async () => {
      try {
        setIsLoading(true);
        if (token) {
          const proposalData = await getHubspotProposalById(token);
          
          setProposal(proposalData);
          
          if (proposalData.status === 'sent' && proposalData.public_token) {
            await updateProposalStatus(proposalData.id, 'viewed');
            setProposal(prev => prev ? { ...prev, status: 'viewed' } : null);
          }
        }
      } catch (error) {
        console.error('Error fetching proposal:', error);
        toast({
          title: 'Erro ao carregar proposta',
          description: 'Não foi possível carregar os dados da proposta.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProposal();
  }, [token]);

  const handleAccept = async () => {
    if (!proposal || !signature) return;
    
    try {
      setIsSubmitting(true);
      
      const signatureDataUrl = signature.toDataURL();
      
      await createProposalSignature({
        proposal_id: proposal.id,
        signer_name: signerName,
        signer_email: signerEmail,
        signer_title: signerTitle,
        signer_company: signerCompany,
        signature_image_url: signatureDataUrl,
        signed_at: new Date().toISOString(),
      });
      
      await updateProposalStatus(proposal.id, 'accepted');
      
      setProposal(prev => prev ? { ...prev, status: 'accepted' } : null);
      setShowSignDialog(false);
      
      toast({
        title: 'Proposta aceita',
        description: 'A proposta foi aceita com sucesso.',
      });
    } catch (error) {
      console.error('Error accepting proposal:', error);
      toast({
        title: 'Erro ao aceitar proposta',
        description: 'Não foi possível aceitar a proposta. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDocuSignAccept = async () => {
    if (!proposal) return;
    
    try {
      setIsSubmitting(true);
      
      const signatureData = {
        proposal_id: proposal.id,
        signer_name: signerName,
        signer_email: signerEmail,
        signer_title: signerTitle,
        signer_company: signerCompany,
        signed_at: new Date().toISOString(),
      };
      
      const docuSignResponse = await signViaDocuSign(signatureData);
      
      await createProposalSignature(signatureData);
      
      await updateProposalStatus(proposal.id, 'viewed');
      
      const mockDocuSignUrl = `https://demo.docusign.net/signing/simulator?envelope_id=${docuSignResponse.docusign_envelope_id}`;
      setDocuSignUrl(mockDocuSignUrl);
      
      setShowDocuSignDialog(false);
      
      toast({
        title: 'Redirecionando para DocuSign',
        description: 'Você será redirecionado para o DocuSign para assinar a proposta.',
      });
      
      window.open(mockDocuSignUrl, '_blank');
      
    } catch (error) {
      console.error('Error initiating DocuSign:', error);
      toast({
        title: 'Erro ao iniciar DocuSign',
        description: 'Não foi possível iniciar o processo de assinatura. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!proposal) return;
    
    try {
      setIsSubmitting(true);
      
      await updateProposalStatus(proposal.id, 'rejected');
      
      setProposal(prev => prev ? { ...prev, status: 'rejected' } : null);
      setShowRejectDialog(false);
      
      toast({
        title: 'Proposta rejeitada',
        description: 'A proposta foi rejeitada.',
      });
    } catch (error) {
      console.error('Error rejecting proposal:', error);
      toast({
        title: 'Erro ao rejeitar proposta',
        description: 'Não foi possível rejeitar a proposta. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadPdf = () => {
    if (proposal?.pdf_url) {
      window.open(proposal.pdf_url, '_blank');
    } else {
      toast({
        title: 'PDF não disponível',
        description: 'O PDF desta proposta ainda não foi gerado.',
        variant: 'destructive',
      });
    }
  };

  const clearSignature = () => {
    if (signature) {
      signature.clear();
    }
  };

  if (isLoading) {
    return (
      <div className="container max-w-4xl mx-auto py-12 flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="container max-w-4xl mx-auto py-12">
        <Card>
          <CardHeader>
            <CardTitle className="text-center">Proposta não encontrada</CardTitle>
            <CardDescription className="text-center">
              A proposta solicitada não existe ou expirou.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-center">
            <Button asChild variant="outline">
              <Link to="/">Voltar para página inicial</Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  const isActionable = ['sent', 'viewed'].includes(proposal.status);

  return (
    <div className="container max-w-4xl mx-auto py-12">
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center mb-2">
            <Badge className={statusColors[proposal.status]}>
              {statusLabels[proposal.status]}
            </Badge>
            {proposal.valid_until && (
              <div className="text-sm text-muted-foreground">
                Válido até {new Date(proposal.valid_until).toLocaleDateString('pt-BR')}
              </div>
            )}
          </div>
          <CardTitle className="text-2xl">{proposal.title}</CardTitle>
          <CardDescription>
            Proposta para {proposal.opportunity.customer.name} - Versão {proposal.version}
          </CardDescription>
        </CardHeader>
        
        <Tabs defaultValue="details" value={activeTab} onValueChange={setActiveTab}>
          <div className="px-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">Detalhes da Proposta</TabsTrigger>
              <TabsTrigger value="pdf" disabled={!proposal.pdf_url}>
                Visualizar PDF
              </TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="details">
            <CardContent className="space-y-6">
              {proposal.description && (
                <div>
                  <h3 className="text-lg font-extrabold mb-2">Descrição</h3>
                  <p className="text-muted-foreground">{proposal.description}</p>
                </div>
              )}

              <div>
                <h3 className="text-lg font-extrabold mb-2">Cliente</h3>
                <div className="space-y-1">
                  <p><span className="font-extrabold">Nome:</span> {proposal.opportunity.customer.name}</p>
                  {proposal.opportunity.customer.business_identification_number && (
                    <p><span className="font-extrabold">CNPJ:</span> {proposal.opportunity.customer.business_identification_number}</p>
                  )}
                  {proposal.opportunity.customer.legal_name && (
                    <p><span className="font-extrabold">Razão Social:</span> {proposal.opportunity.customer.legal_name}</p>
                  )}
                  {proposal.opportunity.customer.address && (
                    <p>
                      <span className="font-extrabold">Endereço:</span> {proposal.opportunity.customer.address}
                      {proposal.opportunity.customer.city && `, ${proposal.opportunity.customer.city}`}
                      {proposal.opportunity.customer.state && `, ${proposal.opportunity.customer.state}`}
                      {proposal.opportunity.customer.zip && ` - ${proposal.opportunity.customer.zip}`}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-extrabold mb-2">Itens da Proposta</h3>
                <div className="border rounded-md">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-extrabold text-gray-500 uppercase tracking-wider">
                          Descrição
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-extrabold text-gray-500 uppercase tracking-wider">
                          Qtd
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-extrabold text-gray-500 uppercase tracking-wider">
                          Preço Unit.
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-extrabold text-gray-500 uppercase tracking-wider">
                          Desconto
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-extrabold text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {proposal.proposal_items.map((item) => (
                        <tr key={item.id}>
                          <td className="px-6 py-4 whitespace-normal">
                            <div className="text-sm font-extrabold text-gray-900">{item.description}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{item.quantity} {formatUnitMeasure(item.unit_measure)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{formatCurrency(item.unit_price, proposal.currency_code)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{item.discount_percentage}%</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-extrabold text-gray-900">{formatCurrency(item.total_price, proposal.currency_code)}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50">
                        <td colSpan={4} className="px-6 py-4 whitespace-nowrap text-right font-extrabold">
                          Valor Total:
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-extrabold text-gray-900">
                          {formatCurrency(proposal.total_value, proposal.currency_code)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </CardContent>
          </TabsContent>
          
          <TabsContent value="pdf" className="h-[600px]">
            {proposal.pdf_url ? (
              <iframe
                ref={pdfIframeRef}
                src={proposal.pdf_url}
                className="w-full h-full border-0"
                title="Proposta PDF"
              />
            ) : (
              <div className="flex justify-center items-center h-full">
                <p className="text-muted-foreground">PDF não disponível</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
        
        <CardFooter className="flex justify-between pt-4 border-t">
          <div className="flex space-x-2">
            <Button variant="outline" onClick={downloadPdf} disabled={!proposal.pdf_url}>
              <Download className="mr-2 h-4 w-4" />
              Baixar PDF
            </Button>
            
            {proposal.pdf_url && (
              <Button 
                variant="outline"
                onClick={() => setActiveTab(activeTab === 'pdf' ? 'details' : 'pdf')}
              >
                {activeTab === 'pdf' ? 'Voltar aos Detalhes' : 'Visualizar PDF'}
              </Button>
            )}
          </div>
          
          <div className="space-x-2">
            {isActionable && (
              <>
                <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-50" onClick={() => setShowRejectDialog(true)}>
                  <ThumbsDown className="mr-2 h-4 w-4" />
                  Rejeitar
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowDocuSignDialog(true)}
                  className="bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Assinar com DocuSign
                </Button>
                <Button onClick={() => setShowSignDialog(true)}>
                  <ThumbsUp className="mr-2 h-4 w-4" />
                  Assinar Manualmente
                </Button>
              </>
            )}
            
            {proposal.status === 'accepted' && (
              <div className="flex items-center text-green-600 font-extrabold">
                <Check className="mr-2 h-5 w-5" />
                Proposta aceita
              </div>
            )}
            
            {proposal.status === 'rejected' && (
              <div className="flex items-center text-red-600 font-extrabold">
                <ThumbsDown className="mr-2 h-5 w-5" />
                Proposta rejeitada
              </div>
            )}
          </div>
        </CardFooter>
      </Card>
      
      <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Assinar Proposta</DialogTitle>
            <DialogDescription>
              Preencha seus dados e assine para aceitar a proposta.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="signerName">Nome Completo *</Label>
                <Input 
                  id="signerName" 
                  value={signerName} 
                  onChange={(e) => setSignerName(e.target.value)} 
                  placeholder="Seu nome completo"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="signerEmail">Email *</Label>
                <Input 
                  id="signerEmail" 
                  type="email"
                  value={signerEmail} 
                  onChange={(e) => setSignerEmail(e.target.value)} 
                  placeholder="seu.email@exemplo.com"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="signerTitle">Cargo</Label>
                <Input 
                  id="signerTitle" 
                  value={signerTitle} 
                  onChange={(e) => setSignerTitle(e.target.value)} 
                  placeholder="Seu cargo na empresa"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="signerCompany">Empresa</Label>
                <Input 
                  id="signerCompany" 
                  value={signerCompany} 
                  onChange={(e) => setSignerCompany(e.target.value)} 
                  placeholder="Nome da empresa"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Assinatura *</Label>
              <div className="border rounded-md p-2 bg-white">
                <SignatureCanvas
                  ref={(ref) => setSignature(ref)}
                  penColor="black"
                  canvasProps={{
                    width: 500,
                    height: 200,
                    className: 'w-full h-full cursor-crosshair'
                  }}
                />
              </div>
              <Button type="button" variant="outline" size="sm" onClick={clearSignature}>
                Limpar Assinatura
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignDialog(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAccept} 
              disabled={isSubmitting || !signerName || !signerEmail || (signature && signature.isEmpty())}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                <>Aceitar e Assinar</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showDocuSignDialog} onOpenChange={setShowDocuSignDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Assinar com DocuSign</DialogTitle>
            <DialogDescription>
              Preencha seus dados para assinar a proposta utilizando DocuSign.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Importante</AlertTitle>
              <AlertDescription>
                Você será redirecionado para o DocuSign para completar o processo de assinatura eletrônica.
              </AlertDescription>
            </Alert>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="docusignName">Nome Completo *</Label>
                <Input 
                  id="docusignName" 
                  value={signerName} 
                  onChange={(e) => setSignerName(e.target.value)} 
                  placeholder="Seu nome completo"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="docusignEmail">Email *</Label>
                <Input 
                  id="docusignEmail" 
                  type="email"
                  value={signerEmail} 
                  onChange={(e) => setSignerEmail(e.target.value)} 
                  placeholder="seu.email@exemplo.com"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="docusignTitle">Cargo</Label>
                <Input 
                  id="docusignTitle" 
                  value={signerTitle} 
                  onChange={(e) => setSignerTitle(e.target.value)} 
                  placeholder="Seu cargo na empresa"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="docusignCompany">Empresa</Label>
                <Input 
                  id="docusignCompany" 
                  value={signerCompany} 
                  onChange={(e) => setSignerCompany(e.target.value)} 
                  placeholder="Nome da empresa"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocuSignDialog(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button 
              onClick={handleDocuSignAccept} 
              disabled={isSubmitting || !signerName || !signerEmail}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                <>Continuar para DocuSign</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar Proposta</DialogTitle>
            <DialogDescription>
              Por favor, informe o motivo da rejeição desta proposta.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rejectionReason">Motivo da Rejeição</Label>
              <Textarea 
                id="rejectionReason" 
                value={rejectionReason} 
                onChange={(e) => setRejectionReason(e.target.value)} 
                placeholder="Informe o motivo da rejeição desta proposta"
                className="min-h-[100px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject} 
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                <>Confirmar Rejeição</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicProposalView; 